import React, { useMemo, useState, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'
import {
  FormElement,
  Select,
  GridCell,
  InputText,
  InputNumber,
  INPUT_TEXT_VARIANT_DANGER,
} from 'mailup-web-components'
import { filterFormImmerAtom } from '../../state/state'
import { type ConditionProps } from '../../types'
import { type DateMatch } from '../../../../types/FilterCondition'
import {
  dateMatchGroups,
  dateMatchOpts,
  PROFILE_ADDITIONAL_FIELDS,
} from '../../utils/conditionOptions'
import {
  dateMatchHasNoValue,
  dateMatchHasValueDateString,
  dateMatchHasValueInteger,
  validateUiDateSpec,
} from '../../utils/profileFieldSpecValidation'
import { type UiDateSpec } from '../../filterConditionTypes'

const isValid = (spec: UiDateSpec): boolean => {
  return validateUiDateSpec(spec)
}

const DateSpecCondition: FC<ConditionProps> = ({ blockIndex, condIndex }) => {
  const intl = useIntl()
  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const spec = condition.Spec as UiDateSpec

  const matchOptions = useMemo(() => dateMatchOpts(intl), [intl])
  const matchGroups = useMemo(() => dateMatchGroups(intl), [intl])
  const [isValueDirty, setIsValueDirty] = useState(false)

  const handleOperatorChange = (op: DateMatch): void => {
    const defaultValue = dateMatchHasNoValue(op)
      ? undefined
      : dateMatchHasValueInteger(op)
        ? 0
        : ''
    setFilter((draft) => {
      const spec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as UiDateSpec
      spec.Match = op
      spec.Value = defaultValue
    })
    setIsValueDirty(false)
  }

  const handleValueStringChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const valueString = e.target.value
    handleValueChange(valueString)
  }

  const handleValueChange = (value: string | number): void => {
    setIsValueDirty(true)
    setFilter((draft) => {
      const spec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as UiDateSpec
      spec.Value = value
    })
  }

  const invalidValueMessage =
    !dateMatchHasNoValue(spec.Match) &&
    (spec.Value === undefined || spec.Value === '')
      ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
      : dateMatchHasValueDateString(spec.Match)
        ? intl.formatMessage({ id: 'Load.Filters.InvalidDate' })
        : dateMatchHasValueInteger(spec.Match)
          ? intl.formatMessage({ id: 'Load.Filters.InvalidInteger' })
          : undefined
  const feedback =
    isValueDirty && !isValid(spec) ? invalidValueMessage : undefined

  return (
    <>
      <GridCell span={3}>
        <FormElement>
          <Select
            placeholder={intl.formatMessage({
              id: 'Load.Filters.OperatorPlaceholder',
            })}
            options={matchOptions}
            optionGroups={matchGroups}
            value={spec.Match}
            onChange={handleOperatorChange}
            data-mu-test='input-select-operator'
          />
        </FormElement>
      </GridCell>
      {spec.Match !== undefined && !dateMatchHasNoValue(spec.Match) && (
        <GridCell span={3}>
          <FormElement
            data-mu-test='filterForm-UiDateSpec-values_element'
            feedback={feedback}
            hint={
              spec.Identifier === PROFILE_ADDITIONAL_FIELDS.SUBS_DATE &&
              dateMatchHasValueDateString(spec.Match)
                ? intl.formatMessage({
                    id: 'Load.Filters.SubscriptionDateFormatTips',
                  })
                : undefined
            }
          >
            {dateMatchHasValueDateString(spec.Match) && (
              <InputText
                data-mu-test='filterForm-UiDateSpec-values_input'
                name='UiDateSpec-values'
                value={spec.Value}
                onChange={handleValueStringChange}
                placeholder={intl.formatMessage({
                  id: 'Load.Filters.InsertValuePlaceholder',
                })}
                variant={
                  feedback !== undefined ? INPUT_TEXT_VARIANT_DANGER : undefined
                }
              />
            )}
            {dateMatchHasValueInteger(spec.Match) && (
              <InputNumber
                data-mu-test='filterForm-UiDateSpec-values_input'
                name='UiDateSpec-values'
                value={spec.Value}
                onChange={handleValueChange}
                placeholder={intl.formatMessage({
                  id: 'Load.Filters.InsertValuePlaceholder',
                })}
                variant={
                  feedback !== undefined ? INPUT_TEXT_VARIANT_DANGER : undefined
                }
                maxLength={8}
                precision={0}
                step={1}
                pattern={'\\d+'}
              />
            )}
          </FormElement>
        </GridCell>
      )}
    </>
  )
}

export default DateSpecCondition
